/**
 * useFreeGrantBalances Hook
 *
 * Fetches the free-grant meter balances for a free-grant automation project
 * from the ARM control-plane endpoint:
 *   `GET {armResourceId}/freeGrantBalances?api-version=2026-04-01-preview`
 *
 * The balances call is an ARM control-plane request, so it needs the project's
 * *full* ARM resource id
 * (`/subscriptions/.../resourceGroups/.../providers/Microsoft.Logic/automationProjects/<name>`).
 * In local / E2E mode the project list already carries a full ARM id, but in
 * cloud mode the regional data-plane returns a short internal id
 * (`/automationProjects/<guid>`) that ARM rejects with `MissingSubscription`.
 * For that case we resolve the canonical ARM id via Resource Graph — the same
 * lookup the Azure portal deep-links use (`useAutomationProjectArmRefs`).
 *
 * The request is gated on the project being a free-grant project
 * (`getIsFreeProject(project)`) and having a resolved ARM resource id, so it is
 * a no-op for regular (paid) projects. Balances change as the project runs
 * workflows, so the query uses a short stale time.
 */

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useAutomationProjectArmRefs, armRefKey } from '@/hooks/useAzureResources';
import { getFreeGrantBalances } from '@/services/projectService';
import { getIsFreeProject, type AutomationProject, type FreeGrantBalance } from '@/types/automation';

export interface UseFreeGrantBalancesReturn {
  /** Meter balances. Empty while loading, on error, or for non-free projects. */
  balances: FreeGrantBalance[];
  isLoading: boolean;
  error: Error | null;
  /** Fire-and-forget refetch. Returns `void` so callers can't create floating promises. */
  refetch: () => void;
}

/**
 * Resolve the full ARM resource id for a project.
 *
 * - When `project.id` is already a full ARM id (local / E2E mock control plane,
 *   or any path that begins with `/subscriptions/`), use it directly.
 * - Otherwise (cloud data-plane short id, `/automationProjects/<guid>`), look up
 *   the canonical ARM id from the Resource Graph-backed ref map, keyed by
 *   `<location>/<name>`.
 *
 * Returns `undefined` until an ARM id can be resolved, which keeps the query
 * disabled rather than issuing a request that would fail with `MissingSubscription`.
 */
function resolveArmResourceId(
  project: AutomationProject | null | undefined,
  armRefs: Map<string, { armId: string }> | undefined,
): string | undefined {
  if (!project) return undefined;
  if (project.id && project.id.toLowerCase().startsWith('/subscriptions/')) {
    return project.id;
  }
  return armRefs?.get(armRefKey(project.location ?? '', project.name))?.armId;
}

export function useFreeGrantBalances(
  project: AutomationProject | null | undefined,
  enabled = true,
): UseFreeGrantBalancesReturn {
  const { getArmToken } = useAuth();

  const isFree = getIsFreeProject(project);

  // A cloud short-id project must resolve its full ARM id via Resource Graph; a
  // local/E2E project already carries a `/subscriptions/...` id and needs no
  // lookup. Compute this *before* the resolver hook so the tenant-wide Resource
  // Graph request only fires when a lookup is actually required — projects that
  // already carry a full ARM id (and paid/disabled projects) skip it entirely,
  // keeping the impact to the single documented balances GET.
  const needsArmRefLookup =
    enabled && isFree && !!project && !project.id?.toLowerCase().startsWith('/subscriptions/');

  // Only resolve ARM refs (a Resource Graph lookup) when a lookup is actually
  // required. For paid/disabled projects, or projects that already carry a
  // `/subscriptions/...` id, skip it entirely rather than firing an unnecessary
  // network dependency.
  const {
    data: projectArmRefs,
    isLoading: armRefsLoading,
    error: armRefsError,
    refetch: armRefsRefetch,
  } = useAutomationProjectArmRefs(needsArmRefLookup);

  const armResourceId = resolveArmResourceId(project, projectArmRefs);

  // When a lookup is required but the id is still unresolved, the ARM-ref
  // query's own loading/error state is what the UI should reflect.
  const resolvingArmRef = needsArmRefLookup && !armResourceId;
  const canFetch = enabled && isFree && !!armResourceId;

  const query = useQuery<FreeGrantBalance[], Error>({
    queryKey: ['free-grant-balances', armResourceId],
    queryFn: () => getFreeGrantBalances(armResourceId as string, getArmToken),
    enabled: canFetch,
    staleTime: 60 * 1000, // 1 minute — balances drift as workflows run
    retry: 1,
  });

  const { refetch: queryRefetch } = query;
  const refetch = useCallback(() => {
    // When the ARM id resolved, retry the balances query. When resolution is
    // still pending/failed, retry the Resource Graph lookup instead. Never call
    // the balances query while disabled — that would invoke `getFreeGrantBalances`
    // with an invalid id.
    if (canFetch) {
      void queryRefetch();
    } else if (resolvingArmRef) {
      void armRefsRefetch();
    }
  }, [canFetch, resolvingArmRef, queryRefetch, armRefsRefetch]);

  // Surface the resolver's loading/error while we're still resolving the ARM id,
  // so a Resource Graph failure (or a completed lookup that found no match) is
  // reported instead of being silently rendered as an empty tab.
  const resolutionError: Error | null = resolvingArmRef && !armRefsLoading
    ? armRefsError ?? new Error('Could not resolve the automation project resource id.')
    : null;

  // Honor the hook contract for non-free / disabled projects: report empty
  // balances and no error even if the query cache still holds data from a
  // previous enabled run for the same key.
  return {
    balances: canFetch ? query.data ?? [] : [],
    isLoading: (canFetch && query.isLoading) || (resolvingArmRef && armRefsLoading),
    error: canFetch ? query.error ?? null : resolutionError,
    refetch,
  };
}
